import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AddressConstants } from '../../../../modules/address/address.constants';
import {
	editPartnerRegStart,
	fetchUFCitiesStart,
	fetchCnpjAvailabilityStart,
	fetchPartnerRegStart,
	createPartnerStart,
	restoreFormReset,
} from '../../state/organizations.actions';
import {
	selectCities,
	selectCnpjAvailability,
	selectPartner,
	selectRestoreForm,
} from '../../state/organizations.selector';

@Component({
	selector: 'app-partner-registration',
	styleUrls: ['./partner-registration.component.scss'],
	templateUrl: './partner-registration.component.html',
})
export class PartnerRegistrationComponent implements OnInit, OnDestroy {
	activeDialogIdx: number = -1;
	auxStateForm!: boolean;
	cities: string[] = [];
	cnpjPattern = '[0-9]{14}';
	form: UntypedFormGroup;
	inputId: string = String(Math.random());
	loading: boolean = true;

	orgId: string = '';
	partnerId: string = '';
	uf = AddressConstants.ufLabels;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly store: Store,
	) {
		this.form = this.fb.group({
			city: [{ disabled: true, value: null }],
			cnpj: [
				null,
				[
					Validators.required,
					Validators.minLength(1),
					Validators.pattern(this.cnpjPattern),
				],
			],
			dental_plan: [false],
			health_plan: [false],
			municipal_registration: [null],
			name: [null, [Validators.required]],
			name_secondary: [null, [Validators.required]],
			state: [null],
			state_registration: [null],
		});
		this.form.get('city')!.disable();
	}

	invalidPlans(): boolean {
		const h = this.form.get('health_plan')!.value;
		const d = this.form.get('dental_plan')!.value;

		return !h && !d;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, pId }) => {
			if (!id || !pId) {
				this.auxStateForm = true;
				this.loading = false;
			} else {
				this.orgId = id;
				this.partnerId = pId;
				this.store.dispatch(fetchPartnerRegStart({ id: pId }));
				this.store
					.select(selectPartner)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((p) => {
						if (p) {
							this.form = this.fb.group({
								city: [{ disabled: true, value: p.city }],
								cnpj: [
									{ disabled: true, value: p.cnpj },
									[Validators.required, Validators.pattern(this.cnpjPattern)],
								],
								dental_plan: [p.dental_plan],
								health_plan: [p.health_plan],
								municipal_registration: [p.municipal_registration],
								name: [p.name, [Validators.required]],
								name_secondary: [p.name_secondary, [Validators.required]],
								state: [p.state],
								state_registration: [p.state_registration],
							});
							this.auxStateForm = false;
							this.loading = false;
						}
					});
			}
		});
		this.store
			.select(selectCities)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((cities) => {
				if (cities.length) {
					this.cities = cities;
					this.form.get('city')!.enable();
				}
			});
	}

	onCnpjBlur(): void {
		if (this.form.get('cnpj')!.valid) {
			const cnpj: string = this.form.get('cnpj')!.value;
			this.store.dispatch(
				fetchCnpjAvailabilityStart({ category: 'partner', cnpj }),
			);
			this.store
				.select(selectCnpjAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (!result) {
						this.form.get('cnpj')!.setErrors({ minlength: true });
					} else {
						this.form.get('cnpj')!.updateValueAndValidity();
					}
				});
		}
	}

	onSubmit(): void {
		if (this.form.valid) {
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.loading = false;
					}
				});
			this.form.get('cnpj')!.enable();
			const request = this.form.value;

			if (this.partnerId) {
				this.form.get('cnpj')!.disable();
				this.store.dispatch(
					editPartnerRegStart({ id: this.partnerId, request }),
				);
			} else {
				this.store.dispatch(createPartnerStart({ request }));
			}
			this.loading = true;
		}
	}

	onUFChange(id: number): void {
		this.form.get('city')!.setValue(null);
		this.form.get('city')!.disable();
		this.auxStateForm = true;
		if (id !== -1) {
			this.store.dispatch(fetchUFCitiesStart({ id }));
		}
	}

	shouldDisableSaveBtn(): boolean {
		return this.form.invalid || this.form.untouched;
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
